import React, { useState, useEffect } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import './ProductTour.css';

const ProductTour = ({ run, onComplete, onSkip }) => {
    const [stepIndex, setStepIndex] = useState(0);

    const steps = [
        {
            target: '.panel-left',
            content: (
                <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        Start Here: Requirements
                    </h3>
                    <p>Read the challenge requirements carefully. Understanding what you need to design is the first step to creating a great solution.</p>
                </div>
            ),
            disableBeacon: true,
            placement: 'right',
        },
        {
            target: '.canvas-container',
            content: (
                <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        Draw Your Design
                    </h3>
                    <p>Use the canvas to create your system architecture diagram. Click anywhere to start drawing. You can use shapes, arrows, and text to express your ideas.</p>
                </div>
            ),
            placement: 'center',
            spotlightClicks: true,
            disableOverlay: false,
            styles: {
                spotlight: {
                    borderRadius: '8px',
                },
            },
        },
        {
            target: '.timer-container',
            content: (
                <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                        Track Your Time
                    </h3>
                    <p>The timer helps you practice working within time constraints - a valuable skill in real system design interviews.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '.btn-secondary',
            content: (
                <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                            <polyline points="17 21 17 13 7 13 7 21" />
                            <polyline points="7 3 7 8 15 8" />
                        </svg>
                        Save Your Progress
                    </h3>
                    <p>Click here anytime to save your work. Don't worry - your design also auto-saves every few seconds!</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '.btn-primary',
            content: (
                <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M22 2L11 13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                        Submit for Evaluation
                    </h3>
                    <p>When you're ready, submit your design to get instant AI-powered feedback on your architecture, scalability, and best practices.</p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: 'body',
            content: (
                <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                        </svg>
                        Pro Tip: Fullscreen Mode
                    </h3>
                    <p>Press <strong>F11</strong> or use your browser's fullscreen option for a distraction-free design experience. This gives you more canvas space to work with!</p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
        },
    ];

    const handleJoyrideCallback = (data) => {
        const { status, action, index, type } = data;

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            // Tour completed or skipped
            if (status === STATUS.FINISHED) {
                onComplete?.();
            } else if (status === STATUS.SKIPPED) {
                onSkip?.();
            }
            setStepIndex(0);
        } else if (type === 'step:after' || type === 'target:not-found') {
            // Move to next step
            setStepIndex(index + (action === 'prev' ? -1 : 1));
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            continuous
            showProgress
            showSkipButton
            stepIndex={stepIndex}
            callback={handleJoyrideCallback}
            styles={{
                options: {
                    arrowColor: '#2d2d2d',
                    backgroundColor: '#2d2d2d',
                    overlayColor: 'rgba(0, 0, 0, 0.8)',
                    primaryColor: '#ff6b35',
                    textColor: '#e0e0e0',
                    width: 380,
                    zIndex: 10000,
                },
                tooltip: {
                    borderRadius: '8px',
                    padding: '20px',
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                tooltipContent: {
                    padding: '10px 0',
                },
                buttonNext: {
                    backgroundColor: '#ff6b35',
                    fontSize: '14px',
                    padding: '8px 16px',
                    borderRadius: '4px',
                },
                buttonBack: {
                    color: '#b0b0b0',
                    fontSize: '14px',
                    marginRight: '10px',
                },
                buttonSkip: {
                    color: '#888',
                    fontSize: '13px',
                },
                buttonClose: {
                    display: 'none',
                },
            }}
            locale={{
                back: 'Previous',
                close: 'Close',
                last: 'Finish Tour',
                next: 'Next',
                skip: 'Skip Tour',
            }}
            floaterProps={{
                disableAnimation: false,
            }}
        />
    );
};

export default ProductTour;
